// Regras da cobrança unificada: juntar várias faturas em aberto do mesmo
// cliente num boleto avulso só, que ao ser pago quita todas de uma vez.
//
// Quem de fato quita é o banco — o trigger `trg_quitar_por_cobranca_unificada`
// (migration 20260917140000), que reage ao pagamento venha ele do webhook de
// baixa do Sicoob, do Asaas ou da confirmação manual. Aqui ficam só as decisões
// de quem monta a negociação, antes do boleto existir.
//
// Função pura, sem I/O nem JSX: roda sob `node --test` com type-stripping.

import { derivarStatusBoleto, valorDevido, type DadosStatusBoleto } from './statusBoleto.ts';

/** A fatura como a tela da inadimplência a conhece — só o que a regra precisa. */
export interface FaturaParaUnificar extends DadosStatusBoleto {
  id: string | number;
  uc_codigo?: string | null;
  mes_referencia?: string | null;
  cliente_nome?: string | null;
  cliente_cpf_cnpj?: string | null;
  num_cobranca_boleto?: string | null;
}

/**
 * Motivos da negociação. Lista fechada porque o financeiro precisa AGRUPAR isso
 * no relatório de eficiência — o pedido da Meryene em 17/09/2026 foi justamente
 * conseguir mostrar que a fatura só foi quitada porque alguém entrou em contato.
 * Texto livre não responde essa pergunta sem alguém ler uma a uma.
 */
export const MOTIVOS_NEGOCIACAO = [
  'Negociação ativa (nós procuramos)',
  'Cliente procurou a Vigor',
  'Acordo em visita de cobrança',
  'Cobrança agrupada a pedido do cliente',
  'Recuperação de débito antigo',
] as const;

export type MotivoNegociacao = (typeof MOTIVOS_NEGOCIACAO)[number];

/** Por onde o contato aconteceu. Mesma razão da lista de motivos. */
export const CANAIS_CONTATO = [
  'WhatsApp',
  'Telefone',
  'E-mail',
  'Visita presencial',
  'Presencial no escritório',
] as const;

export type CanalContato = (typeof CANAIS_CONTATO)[number];

/** Status que ainda representam dívida viva — o único universo unificável. */
const STATUS_UNIFICAVEIS = ['Vencido', 'Em aberto'];

/**
 * A fatura pode entrar numa cobrança unificada?
 *
 * Deriva o status em vez de ler `status_boleto_vigor` cru pelo mesmo motivo de
 * sempre: o campo mente (fatura sem boleto calculado, valor zero, ou já paga com
 * o status desatualizado). Ver o cabeçalho de `statusBoleto.ts`.
 */
export function faturaUnificavel(f: FaturaParaUnificar, hoje: Date = new Date()): boolean {
  return STATUS_UNIFICAVEIS.includes(derivarStatusBoleto(f, hoje));
}

/** Soma do que as faturas selecionadas devem. Usa `valorDevido` porque em boleto
 *  único / parceira o valor NÃO está em `valor_boleto_vigor`. */
export function totalDasFaturas(faturas: FaturaParaUnificar[]): number {
  return faturas.reduce((soma, f) => soma + valorDevido(f), 0);
}

/** Arredonda para centavos — evita que a soma de vários float feche 249.99999. */
export function emCentavos(valor: number): number {
  return Math.round(valor * 100) / 100;
}

export interface DadosCobrancaUnificada {
  faturas: FaturaParaUnificar[];
  /** O que vai para o boleto. Igual à soma, salvo desconto/acréscimo negociado. */
  valorCobrado: number;
  motivo: string;
  canal: string;
  observacao: string;
}

export interface Validacao {
  ok: boolean;
  /** Impedem a emissão. */
  erros: string[];
  /** Não impedem: a decisão é de quem negocia, mas precisa estar à vista. */
  avisos: string[];
}

/** Diferença acima da qual a tela exige justificativa explícita no texto. */
export const TOLERANCIA_DIFERENCA = 0.05;

/**
 * Valida a negociação antes de gerar o boleto.
 *
 * O que é ERRO aqui é o que produziria cobrança errada ou registro inútil; o
 * resto é aviso. Juntar UCs diferentes do mesmo titular, por exemplo, é normal
 * e desejado — a dívida é do cliente, não da UC (mesma decisão tomada no acordo
 * de parcelamento).
 */
export function validarCobrancaUnificada(
  dados: DadosCobrancaUnificada,
  hoje: Date = new Date(),
): Validacao {
  const erros: string[] = [];
  const avisos: string[] = [];

  if (dados.faturas.length < 2) {
    erros.push('Selecione pelo menos duas faturas — para uma só, use a reemissão normal do boleto.');
  }

  const naoUnificaveis = dados.faturas.filter((f) => !faturaUnificavel(f, hoje));
  if (naoUnificaveis.length > 0) {
    erros.push(
      `${naoUnificaveis.length} fatura(s) não estão em aberto nem vencidas e não podem entrar: `
      + naoUnificaveis.map((f) => f.mes_referencia || f.id).join(', ')
      + '.',
    );
  }

  if (!(dados.valorCobrado > 0)) {
    erros.push('O valor do boleto precisa ser maior que zero.');
  }

  if (!MOTIVOS_NEGOCIACAO.includes(dados.motivo as MotivoNegociacao)) {
    erros.push('Escolha o motivo da negociação.');
  }

  if (!CANAIS_CONTATO.includes(dados.canal as CanalContato)) {
    erros.push('Escolha o canal do contato.');
  }

  const observacao = (dados.observacao || '').trim();
  if (observacao.length < 10) {
    erros.push('Escreva uma observação com pelo menos 10 caracteres — é o que explica a quitação no relatório.');
  }

  const total = emCentavos(totalDasFaturas(dados.faturas));
  const diferenca = emCentavos(dados.valorCobrado - total);

  if (Math.abs(diferenca) > TOLERANCIA_DIFERENCA) {
    const rotulo = diferenca < 0 ? 'desconto' : 'acréscimo';
    avisos.push(
      `O boleto sai R$ ${Math.abs(diferenca).toFixed(2)} de ${rotulo} sobre a soma das faturas `
      + `(R$ ${total.toFixed(2)}). Deixe o motivo claro na observação.`,
    );
  }

  const titulares = new Set(
    dados.faturas.map((f) => (f.cliente_cpf_cnpj || '').replace(/\D/g, '') || `nome:${(f.cliente_nome || '').trim().toUpperCase()}`),
  );
  if (titulares.size > 1) {
    erros.push('As faturas são de titulares diferentes. Um boleto só pode cobrar um pagador.');
  }

  const ucs = new Set(dados.faturas.map((f) => String(f.uc_codigo || '')));
  if (ucs.size > 1) {
    avisos.push(`A cobrança junta ${ucs.size} UCs do mesmo titular — o boleto sai em nome dele.`);
  }

  const semBoleto = dados.faturas.filter((f) => !f.num_cobranca_boleto);
  if (semBoleto.length > 0) {
    avisos.push(
      `${semBoleto.length} fatura(s) não têm boleto registrado no banco: elas serão quitadas, `
      + 'mas não há título para baixar no Sicoob.',
    );
  }

  return { ok: erros.length === 0, erros, avisos };
}

/** Descrição que vai no boleto e no histórico — os meses que estão sendo cobrados. */
export function descricaoCobranca(faturas: FaturaParaUnificar[]): string {
  const meses = faturas.map((f) => f.mes_referencia).filter(Boolean);
  return `Cobrança unificada — ${meses.join(', ')}`;
}

/**
 * Snapshot das faturas para `cobrancas_unificadas.faturas_snapshot`.
 * A fatura muda depois (status, valor, boleto); o relatório precisa do que valia
 * no dia da negociação. Mesma razão de `parcelamentos.faturas_origem_snapshot`.
 */
export function snapshotDasFaturas(faturas: FaturaParaUnificar[], hoje: Date = new Date()) {
  return faturas.map((f) => ({
    id: f.id,
    uc_codigo: f.uc_codigo,
    mes_referencia: f.mes_referencia,
    valor: emCentavos(valorDevido(f)),
    vencimento: f.vencimento_boleto || f.data_vencimento || null,
    status: derivarStatusBoleto(f, hoje),
    num_cobranca_boleto: f.num_cobranca_boleto || null,
  }));
}
